'use client'

import { useState, useEffect } from 'react'
import { sanityFetch } from '@utils/fetch'

import { BlogGrid } from 'shared-components'

import type { BlogGrid as BlogGridType } from '@/types/sanity.types'

async function fetchInitialData(featurePostSlug?: string) {
    return await sanityFetch({
        query: `{
            "posts": *[_type == 'post' && slug.current != null ${featurePostSlug ? '&& slug.current != $featurePostSlug' : ''}] | order(_createdAt desc)[0...24]{
                ...,
                "slug": slug.current,
                category-> {
                    name,
                    "slug": slug.current,
                },
                featuredImage {
                    ...,
                    asset-> {
                        url,
                        metadata {
                            lqip,
                        }
                    }
                },
            },
            "categories": *[_type == 'category'] | order(name asc) {
                name,
                "slug": slug.current,
            },
            "totalPosts": count(*[_type == 'post' && slug.current != null ${featurePostSlug ? '&& slug.current != $featurePostSlug' : ''}]),
        }`,
        params: featurePostSlug ? { featurePostSlug } : {},
        tags: [`post`],
    })
}

async function fetchMorePosts(start: number, featurePostSlug?: string) {
    return await sanityFetch({
        query: `{
            "posts": *[_type == 'post' && slug.current != null ${featurePostSlug ? '&& slug.current != $featurePostSlug' : ''}] | order(_createdAt desc)[${start}...${start + 24}]{
                ...,
                "slug": slug.current,
                category-> {
                    name,
                    "slug": slug.current,
                },
                featuredImage {
                    ...,
                    asset-> {
                        url,
                        metadata {
                            lqip,
                        }
                    }
                },
            },
        }`,
        params: featurePostSlug ? { featurePostSlug } : {},
        tags: [`post`],
    })
}

async function fetchPostsByCategory(category: string, featurePostSlug?: string) {
    if (!category) {
        return fetchInitialData(featurePostSlug)
    }
    return await sanityFetch({
        query: `{
            "posts": *[_type == 'post' && slug.current != null ${featurePostSlug ? '&& slug.current != $featurePostSlug' : ''} && category->slug.current == $category] | order(_createdAt desc){
                ...,
                "slug": slug.current,
                category-> {
                    name,
                    "slug": slug.current,
                },
                featuredImage {
                    ...,
                    asset-> {
                        url,
                        metadata {
                            lqip,
                        }
                    }
                },
            },
        }`,
        params: featurePostSlug ? { category, featurePostSlug } : { category },
        tags: [`post`, `category:${category}`],
    })
}

type ExtendedBlogGridType = BlogGridType & {
    featurePost: any
}

function BlogGridCont({ title, featurePost }: ExtendedBlogGridType) {
    const [posts, setPosts] = useState<any[]>([])
    const [categories, setCategories] = useState<any[]>([])
    const [totalPosts, setTotalPosts] = useState<number>(0)
    const [start, setStart] = useState(24)

    useEffect(() => {
        fetchInitialData(featurePost?.slug?.current).then((data) => {
            setPosts(data?.posts || [])
            setCategories(data?.categories || [])
            setTotalPosts(data?.totalPosts || 0)
        })
    }, [featurePost])

    const handleClick = async () => {
        const morePosts = await fetchMorePosts(start, featurePost?.slug?.current)
        setPosts((prevPosts) => [...prevPosts, ...morePosts.posts])
        setStart((prevStart) => prevStart + 24)
    }

    const getPostsByCategory = async (category: string) => {
        const postsByCategory = await fetchPostsByCategory(category, featurePost?.slug?.current)
        setPosts(postsByCategory.posts)
    }

    return (
        <BlogGrid
            title={title}
            posts={posts}
            totalPosts={totalPosts}
            featurePost={featurePost}
            categories={categories}
            showMore={handleClick}
            getPostsByCategory={getPostsByCategory}
        />
    )
}

export default BlogGridCont
